// src/context.tsx
import { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { USoftwareClient } from "@usoftware/auth-core";
import { jsx } from "react/jsx-runtime";
var AuthContext = createContext(null);
function AuthProvider({
  clientId,
  redirectUri,
  baseUrl,
  apiUrl,
  scopes,
  children,
  onAuthenticated,
  onError
}) {
  const [state, setState] = useState({ status: "loading" });
  const [error, setError] = useState(null);
  const initRef = useRef(false);
  const client = useMemo(
    () => new USoftwareClient({
      clientId,
      redirectUri,
      baseUrl,
      apiUrl,
      scopes
    }),
    [clientId, redirectUri, baseUrl, apiUrl, scopes]
  );
  useEffect(() => {
    const unsubscribe = client.onAuthChange((newState) => {
      setState(newState);
      if (newState.status === "authenticated" && onAuthenticated) {
        onAuthenticated(newState.user);
      }
    });
    if (initRef.current) {
      return unsubscribe;
    }
    initRef.current = true;
    const params = new URLSearchParams(window.location.search);
    if (params.has("code")) {
      client.handleCallback().catch((err) => {
        const error2 = err instanceof Error ? err : new Error(String(err));
        setError(error2);
        onError?.(error2);
      });
    } else {
      client.checkSession().catch((err) => {
        const error2 = err instanceof Error ? err : new Error(String(err));
        setError(error2);
        onError?.(error2);
      });
    }
    return unsubscribe;
  }, [client, onAuthenticated, onError]);
  const handleCallback = useCallback(async () => {
    try {
      const user = await client.handleCallback();
      setError(null);
      return user;
    } catch (err) {
      const error2 = err instanceof Error ? err : new Error(String(err));
      setError(error2);
      throw error2;
    }
  }, [client]);
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  const value = useMemo(() => {
    const user = state.status === "authenticated" ? state.user : null;
    const appUser = state.status === "authenticated" ? state.appUser : null;
    return {
      // State
      user,
      appUser,
      isAuthenticated: state.status === "authenticated",
      isAppUser: appUser !== null,
      isLoading: state.status === "loading",
      error,
      // Permissions
      permissions: appUser?.scopes ?? [],
      roles: appUser?.roles ?? [],
      hasScope: (scope) => appUser?.scopes.includes(scope) ?? false,
      hasRole: (role) => appUser?.roles.includes(role) ?? false,
      // Actions
      login: (options) => client.login(options),
      logout: (options) => client.logout(options),
      getAccessToken: () => client.getAccessToken(),
      handleCallback,
      clearError,
      // Step-up and billing
      requestStepUp: (action, paramsHash, level) => client.requestStepUp(action, paramsHash, level),
      requestAgreement: (agreementId) => client.requestAgreement(agreementId),
      // Client access
      client
    };
  }, [state, error, client, handleCallback, clearError]);
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value, children });
}
function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}

// src/hooks/useAuth.ts
function useAuth() {
  return useAuthContext();
}

// src/hooks/useStepUp.ts
import { useState as useState2, useCallback as useCallback2 } from "react";
function useStepUp() {
  const { client } = useAuthContext();
  const [isLoading, setIsLoading] = useState2(false);
  const [error, setError] = useState2(null);
  const openStepUp = useCallback2(
    async (options) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await client.openStepUpChallenge(options.challengeId);
        if (result.success) {
          return {
            success: true,
            token: result.token
          };
        } else {
          const errorMsg = result.error || "Step-up verification failed";
          setError(errorMsg);
          return {
            success: false,
            error: errorMsg
          };
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Step-up verification failed";
        setError(errorMsg);
        return {
          success: false,
          error: errorMsg
        };
      } finally {
        setIsLoading(false);
      }
    },
    [client]
  );
  return {
    openStepUp,
    isLoading,
    error
  };
}

// src/hooks/useAgreement.ts
import { useState as useState3, useCallback as useCallback3 } from "react";
function useAgreement() {
  const { client } = useAuthContext();
  const [isLoading, setIsLoading] = useState3(false);
  const [error, setError] = useState3(null);
  const openAgreement = useCallback3(
    async (options) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await client.requestAgreement(options.agreementId);
        if (result.success) {
          return {
            success: true,
            agreementId: options.agreementId
          };
        } else {
          const errorMsg = result.error || "Agreement approval failed";
          setError(errorMsg);
          return {
            success: false,
            error: errorMsg
          };
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Agreement approval failed";
        setError(errorMsg);
        return {
          success: false,
          error: errorMsg
        };
      } finally {
        setIsLoading(false);
      }
    },
    [client]
  );
  return {
    openAgreement,
    isLoading,
    error
  };
}

// src/hooks/useOrganization.ts
function useOrganization() {
  const { appUser } = useAuthContext();
  if (!appUser?.organizationId) {
    return {
      organization: null,
      roles: null,
      isOrgContext: false
    };
  }
  return {
    organization: {
      id: appUser.organizationId,
      slug: appUser.organizationSlug
    },
    roles: appUser.organizationRoles,
    isOrgContext: true
  };
}

// src/hooks/useOrgAuth.ts
import { useCallback as useCallback4 } from "react";
function useOrgAuth() {
  const { client } = useAuthContext();
  const loginToOrg = useCallback4(
    (orgSlug, options) => client.loginToOrg(orgSlug, options),
    [client]
  );
  const getOrgSession = useCallback4(
    (orgSlug) => client.getOrgSession(orgSlug),
    [client]
  );
  const getOrgOAuthProviders = useCallback4(
    (orgSlug) => client.getOrgOAuthProviders(orgSlug),
    [client]
  );
  const loginToOrgWithOAuth = useCallback4(
    (orgSlug, provider, options) => client.loginToOrgWithOAuth(orgSlug, provider, options),
    [client]
  );
  const logoutFromOrg = useCallback4(
    (orgSlug, options) => client.logoutFromOrg(orgSlug, options),
    [client]
  );
  const getOrgContext = useCallback4(() => client.getOrgContext(), [client]);
  const setOrgContext = useCallback4(
    (orgSlug) => client.setOrgContext(orgSlug),
    [client]
  );
  return {
    loginToOrg,
    getOrgSession,
    getOrgOAuthProviders,
    loginToOrgWithOAuth,
    logoutFromOrg,
    getOrgContext,
    setOrgContext
  };
}

// src/hooks/useOrgInvite.ts
import { useState as useState4, useCallback as useCallback5, useEffect as useEffect2, useRef as useRef2 } from "react";
function useOrgInvite(token, autoValidate = true) {
  const { client } = useAuthContext();
  const [invite, setInvite] = useState4(null);
  const [loading, setLoading] = useState4(autoValidate);
  const [error, setError] = useState4(null);
  const validatedRef = useRef2(false);
  const validate = useCallback5(async () => {
    if (!token) {
      setError("No invitation token provided");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await client.validateInvite(token);
      if (result.valid && result.invite) {
        setInvite(result.invite);
        setError(null);
      } else {
        setInvite(null);
        setError(result.error || "Invalid or expired invitation");
      }
    } catch (err) {
      setInvite(null);
      setError(err instanceof Error ? err.message : "Failed to validate invitation");
    } finally {
      setLoading(false);
    }
  }, [client, token]);
  const accept = useCallback5(async () => {
    if (!token) {
      return {
        success: false,
        error: "No invitation token provided"
      };
    }
    setLoading(true);
    setError(null);
    try {
      const result = await client.acceptInvite(token);
      if (!result.success) {
        setError(result.error || "Failed to accept invitation");
      }
      return result;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to accept invitation";
      setError(errorMsg);
      return {
        success: false,
        error: errorMsg
      };
    } finally {
      setLoading(false);
    }
  }, [client, token]);
  useEffect2(() => {
    if (autoValidate && token && !validatedRef.current) {
      validatedRef.current = true;
      validate();
    }
  }, [autoValidate, token, validate]);
  return {
    invite,
    loading,
    error,
    validate,
    accept
  };
}

// src/components/AuthGuard.tsx
function AuthGuard({
  children,
  loading,
  fallback,
  scopes,
  requiredScopes,
  roles,
  requiredRoles,
  redirectToLogin = false,
  redirectTo
}) {
  const effectiveScopes = scopes ?? requiredScopes;
  const effectiveRoles = roles ?? requiredRoles;
  const { isAuthenticated, isLoading, hasScope, hasRole, login } = useAuth();
  if (isLoading) {
    return loading ?? null;
  }
  if (!isAuthenticated) {
    if (redirectToLogin || redirectTo) {
      login({ redirectTo });
      return loading ?? null;
    }
    return fallback ?? null;
  }
  if (effectiveScopes && effectiveScopes.length > 0) {
    const hasAllScopes = effectiveScopes.every(hasScope);
    if (!hasAllScopes) {
      return fallback ?? null;
    }
  }
  if (effectiveRoles && effectiveRoles.length > 0) {
    const hasAnyRole = effectiveRoles.some(hasRole);
    if (!hasAnyRole) {
      return fallback ?? null;
    }
  }
  return children;
}

// src/components/USignIn.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
function USignIn({
  text = "Sign in with u.software",
  loadingText = "Loading...",
  scopes,
  showLoading = true,
  disabled,
  ...buttonProps
}) {
  const { login, isLoading, isAuthenticated, user, logout } = useAuth();
  const handleClick = () => {
    if (isAuthenticated) {
      return;
    }
    login({ scopes });
  };
  if (isAuthenticated && user) {
    return /* @__PURE__ */ jsx2(
      "button",
      {
        type: "button",
        onClick: () => logout(),
        disabled,
        ...buttonProps,
        children: user.displayName || user.email || "Sign out"
      }
    );
  }
  return /* @__PURE__ */ jsx2(
    "button",
    {
      type: "button",
      onClick: handleClick,
      disabled: disabled || showLoading && isLoading,
      ...buttonProps,
      children: showLoading && isLoading ? loadingText : text
    }
  );
}

// src/index.ts
import { USoftwareClient as USoftwareClient2 } from "@usoftware/auth-core";
export {
  AuthGuard,
  AuthProvider,
  USignIn,
  USoftwareClient2 as USoftwareClient,
  useAgreement,
  useAuth,
  useAuthContext,
  useOrgAuth,
  useOrgInvite,
  useOrganization,
  useStepUp
};
//# sourceMappingURL=index.js.map